/**
 * Performance Reports Service - Industry Standard Application Performance Monitoring
 * 
 * Provides comprehensive performance reporting and analytics including:
 * - Core Web Vitals (LCP, FID, CLS, FCP, TTFB)
 * - Application Performance Metrics (response times, throughput, errors)
 * - User Experience Analytics (page load times, interaction latency)
 * - System Resource Monitoring (CPU, memory, network, storage)
 * - API Performance Tracking (endpoint response times, error rates)
 * - Real User Monitoring (RUM) vs Synthetic monitoring
 * - Performance budgets and SLA tracking
 * - Historical performance trends and benchmarking
 */

const PERFORMANCE_REPORTS_KEY = 'performance-reports-v1';
const PERFORMANCE_METRICS_KEY = 'performance-metrics-v1';
const PERFORMANCE_ALERTS_KEY = 'performance-alerts-v1';

// Industry standard performance thresholds (based on Google Web Vitals and APM best practices)
const PERFORMANCE_THRESHOLDS = {
  coreWebVitals: {
    lcp: { good: 2500, needs_improvement: 4000 }, // Largest Contentful Paint (ms)
    fid: { good: 100, needs_improvement: 300 },   // First Input Delay (ms)
    cls: { good: 0.1, needs_improvement: 0.25 },  // Cumulative Layout Shift
    fcp: { good: 1800, needs_improvement: 3000 }, // First Contentful Paint (ms)
    ttfb: { good: 800, needs_improvement: 1800 }  // Time to First Byte (ms)
  },
  applicationMetrics: {
    responseTime: { good: 200, needs_improvement: 1000 }, // API response time (ms)
    throughput: { good: 1000, needs_improvement: 500 },   // Requests per minute
    errorRate: { good: 0.01, needs_improvement: 0.05 },   // Error rate (%)
    availability: { good: 0.999, needs_improvement: 0.99 } // Uptime (%)
  },
  resourceUtilization: {
    cpu: { good: 70, needs_improvement: 85 },     // CPU usage (%)
    memory: { good: 80, needs_improvement: 90 },  // Memory usage (%)
    disk: { good: 80, needs_improvement: 90 },    // Disk usage (%)
    network: { good: 100, needs_improvement: 500 } // Network latency (ms)
  }
};

// Generate comprehensive performance data
function generatePerformanceData(days = 30) {
  const now = new Date();
  const data = {
    // Core Web Vitals Data
    webVitals: [],
    // Application Performance Metrics
    applicationMetrics: [],
    // Resource Utilization
    resourceMetrics: [],
    // API Performance
    apiMetrics: [],
    // User Experience Metrics
    userExperience: [],
    // Performance Budgets
    budgets: [],
    // Alerts and Issues
    alerts: [],
    // Synthetic vs Real User Monitoring
    monitoring: [],
    // Performance Trends
    trends: []
  };

  // Generate daily performance data
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(now.getDate() - i);
    const dateStr = date.toISOString().slice(0, 10);

    // Core Web Vitals
    const lcp = 1500 + Math.random() * 2000; // 1.5-3.5s
    const fid = 50 + Math.random() * 200;    // 50-250ms
    const cls = Math.random() * 0.3;         // 0-0.3
    const fcp = 1000 + Math.random() * 1500; // 1-2.5s
    const ttfb = 200 + Math.random() * 800;  // 200-1000ms

    data.webVitals.push({
      date: dateStr,
      lcp: Number(lcp.toFixed(0)),
      fid: Number(fid.toFixed(0)),
      cls: Number(cls.toFixed(3)),
      fcp: Number(fcp.toFixed(0)),
      ttfb: Number(ttfb.toFixed(0)),
      score: calculateWebVitalsScore(lcp, fid, cls, fcp, ttfb),
      sampleSize: 800 + Math.floor(Math.random() * 400)
    });

    // Application Performance Metrics
    const responseTime = 150 + Math.random() * 500;
    const throughput = 800 + Math.random() * 400;
    const errorRate = Math.random() * 0.05;
    const availability = 0.995 + Math.random() * 0.005;

    data.applicationMetrics.push({
      date: dateStr,
      responseTime: Number(responseTime.toFixed(0)),
      throughput: Number(throughput.toFixed(0)),
      errorRate: Number(errorRate.toFixed(4)),
      availability: Number(availability.toFixed(4)),
      totalRequests: Math.floor(throughput * 24 * 60), // Daily requests
      successfulRequests: Math.floor(throughput * 24 * 60 * (1 - errorRate)),
      p95ResponseTime: Number((responseTime * 1.5).toFixed(0)),
      p99ResponseTime: Number((responseTime * 2.2).toFixed(0))
    });

    // Resource Utilization
    data.resourceMetrics.push({
      date: dateStr,
      cpu: Number((60 + Math.random() * 25).toFixed(1)),
      memory: Number((70 + Math.random() * 20).toFixed(1)),
      disk: Number((45 + Math.random() * 30).toFixed(1)),
      network: Number((50 + Math.random() * 100).toFixed(0)),
      diskIO: Number((500 + Math.random() * 300).toFixed(0)), // IOPS
      networkThroughput: Number((100 + Math.random() * 50).toFixed(1)) // Mbps
    });

    // User Experience Metrics
    data.userExperience.push({
      date: dateStr,
      pageLoadTime: Number((2000 + Math.random() * 2000).toFixed(0)),
      interactionLatency: Number((100 + Math.random() * 200).toFixed(0)),
      bounceRate: Number((0.25 + Math.random() * 0.30).toFixed(3)),
      timeOnPage: Number((180 + Math.random() * 240).toFixed(0)),
      exitRate: Number((0.15 + Math.random() * 0.25).toFixed(3)),
      userSatisfactionScore: Number((7.5 + Math.random() * 2.0).toFixed(1)) // 1-10 scale
    });
  }

  // Generate API endpoint performance data
  const endpoints = [
    { name: '/api/dashboard', category: 'Dashboard' },
    { name: '/api/reports', category: 'Reports' },
    { name: '/api/analytics', category: 'Analytics' },
    { name: '/api/users', category: 'User Management' },
    { name: '/api/auth', category: 'Authentication' },
    { name: '/api/settings', category: 'Configuration' }
  ];

  data.apiMetrics = endpoints.map(endpoint => ({
    endpoint: endpoint.name,
    category: endpoint.category,
    avgResponseTime: Number((100 + Math.random() * 300).toFixed(0)),
    p95ResponseTime: Number((200 + Math.random() * 500).toFixed(0)),
    p99ResponseTime: Number((400 + Math.random() * 800).toFixed(0)),
    requestsPerMinute: Number((50 + Math.random() * 200).toFixed(0)),
    errorRate: Number((Math.random() * 0.03).toFixed(4)),
    availability: Number((0.997 + Math.random() * 0.003).toFixed(4)),
    totalRequests: Math.floor(50000 + Math.random() * 100000),
    lastUpdated: new Date().toISOString()
  }));

  // Performance Budgets
  data.budgets = [
    {
      metric: 'Page Load Time',
      budget: 3000,
      current: 2650,
      status: 'within_budget',
      trend: 'improving',
      impact: 'medium'
    },
    {
      metric: 'JavaScript Bundle Size',
      budget: 250, // KB
      current: 285,
      status: 'exceeded',
      trend: 'degrading',
      impact: 'high'
    },
    {
      metric: 'API Response Time',
      budget: 200,
      current: 180,
      status: 'within_budget',
      trend: 'stable',
      impact: 'high'
    },
    {
      metric: 'Core Web Vitals Score',
      budget: 90,
      current: 87,
      status: 'within_budget',
      trend: 'improving',
      impact: 'high'
    }
  ];

  // Performance Alerts
  data.alerts = [
    {
      id: 'alert_001',
      severity: 'warning',
      metric: 'Response Time',
      threshold: 500,
      current: 650,
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      status: 'active',
      description: 'API response time exceeded threshold for /api/reports endpoint'
    },
    {
      id: 'alert_002',
      severity: 'critical',
      metric: 'Error Rate',
      threshold: 0.05,
      current: 0.08,
      timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
      status: 'resolved',
      description: 'High error rate detected in authentication service'
    }
  ];

  // Monitoring Comparison (Synthetic vs RUM)
  data.monitoring = {
    synthetic: {
      availability: 0.999,
      responseTime: 245,
      locations: ['US-East', 'US-West', 'Europe', 'Asia'],
      frequency: '1 minute',
      coverage: '24/7'
    },
    realUser: {
      availability: 0.997,
      responseTime: 380,
      userSessions: 15420,
      geographicSpread: 'Global',
      deviceTypes: ['Desktop: 60%', 'Mobile: 35%', 'Tablet: 5%'],
      browsers: ['Chrome: 65%', 'Safari: 20%', 'Firefox: 10%', 'Edge: 5%']
    }
  };

  // Performance Trends
  data.trends = {
    webVitals: {
      lcp: { change: -5.2, trend: 'improving' },
      fid: { change: -12.1, trend: 'improving' },
      cls: { change: 8.3, trend: 'degrading' },
      fcp: { change: -3.1, trend: 'improving' }
    },
    application: {
      responseTime: { change: 2.5, trend: 'stable' },
      throughput: { change: 15.7, trend: 'improving' },
      errorRate: { change: -25.4, trend: 'improving' },
      availability: { change: 0.1, trend: 'stable' }
    },
    business: {
      conversionRate: { change: 3.2, trend: 'improving' },
      bounceRate: { change: -8.1, trend: 'improving' },
      revenueImpact: { value: 12500, change: 5.8, trend: 'improving' }
    }
  };

  return data;
}

// Calculate Web Vitals Score (0-100)
function calculateWebVitalsScore(lcp, fid, cls, fcp, ttfb) {
  let score = 0;
  
  // LCP Score (25% weight)
  if (lcp <= PERFORMANCE_THRESHOLDS.coreWebVitals.lcp.good) score += 25;
  else if (lcp <= PERFORMANCE_THRESHOLDS.coreWebVitals.lcp.needs_improvement) score += 15;
  else score += 5;

  // FID Score (25% weight)
  if (fid <= PERFORMANCE_THRESHOLDS.coreWebVitals.fid.good) score += 25;
  else if (fid <= PERFORMANCE_THRESHOLDS.coreWebVitals.fid.needs_improvement) score += 15;
  else score += 5;

  // CLS Score (25% weight)
  if (cls <= PERFORMANCE_THRESHOLDS.coreWebVitals.cls.good) score += 25;
  else if (cls <= PERFORMANCE_THRESHOLDS.coreWebVitals.cls.needs_improvement) score += 15;
  else score += 5;

  // FCP Score (15% weight)
  if (fcp <= PERFORMANCE_THRESHOLDS.coreWebVitals.fcp.good) score += 15;
  else if (fcp <= PERFORMANCE_THRESHOLDS.coreWebVitals.fcp.needs_improvement) score += 10;
  else score += 3;

  // TTFB Score (10% weight)
  if (ttfb <= PERFORMANCE_THRESHOLDS.coreWebVitals.ttfb.good) score += 10;
  else if (ttfb <= PERFORMANCE_THRESHOLDS.coreWebVitals.ttfb.needs_improvement) score += 6;
  else score += 2;

  return Math.round(score);
}

// State management
function load() {
  try {
    const raw = localStorage.getItem(PERFORMANCE_REPORTS_KEY);
    return raw ? JSON.parse(raw) : generatePerformanceData(30);
  } catch {
    return generatePerformanceData(30);
  }
}

let state = load();
const subscribers = new Set();

function save() {
  try {
    localStorage.setItem(PERFORMANCE_REPORTS_KEY, JSON.stringify(state));
  } catch {}
}

function publish() {
  save();
  subscribers.forEach(cb => {
    try {
      cb(state);
    } catch {}
  });
}

// Public API
export function subscribe(callback) {
  subscribers.add(callback);
  callback(state);
  return () => subscribers.delete(callback);
}

export function getWebVitalsData(options = {}) {
  const { dateFrom, dateTo } = options;
  let data = [...state.webVitals];

  if (dateFrom) {
    data = data.filter(item => new Date(item.date) >= new Date(dateFrom));
  }
  if (dateTo) {
    data = data.filter(item => new Date(item.date) <= new Date(dateTo));
  }

  return data;
}

export function getApplicationMetrics(options = {}) {
  const { dateFrom, dateTo } = options;
  let data = [...state.applicationMetrics];

  if (dateFrom) {
    data = data.filter(item => new Date(item.date) >= new Date(dateFrom));
  }
  if (dateTo) {
    data = data.filter(item => new Date(item.date) <= new Date(dateTo));
  }

  return data;
}

export function getResourceMetrics() {
  return state.resourceMetrics || [];
}

export function getAPIMetrics() {
  return state.apiMetrics || [];
}

export function getUserExperienceMetrics() {
  return state.userExperience || [];
}

export function getPerformanceBudgets() {
  return state.budgets || [];
}

export function getPerformanceAlerts() {
  return state.alerts || [];
}

export function getMonitoringComparison() {
  return state.monitoring || {};
}

export function getPerformanceTrends() {
  return state.trends || {};
}

export function getPerformanceSummary(days = 7) {
  const recentWebVitals = state.webVitals.slice(-days);
  const recentAppMetrics = state.applicationMetrics.slice(-days);
  const recentUserExp = state.userExperience.slice(-days);

  if (recentWebVitals.length === 0) return null;

  const avgWebVitalsScore = recentWebVitals.reduce((sum, day) => sum + day.score, 0) / recentWebVitals.length;
  const avgResponseTime = recentAppMetrics.reduce((sum, day) => sum + day.responseTime, 0) / recentAppMetrics.length;
  const avgAvailability = recentAppMetrics.reduce((sum, day) => sum + day.availability, 0) / recentAppMetrics.length;
  const avgPageLoadTime = recentUserExp.reduce((sum, day) => sum + day.pageLoadTime, 0) / recentUserExp.length;
  const avgErrorRate = recentAppMetrics.reduce((sum, day) => sum + day.errorRate, 0) / recentAppMetrics.length;

  return {
    webVitalsScore: Number(avgWebVitalsScore.toFixed(1)),
    responseTime: Number(avgResponseTime.toFixed(0)),
    availability: Number((avgAvailability * 100).toFixed(2)),
    pageLoadTime: Number(avgPageLoadTime.toFixed(0)),
    errorRate: Number((avgErrorRate * 100).toFixed(3)),
    totalRequests: recentAppMetrics.reduce((sum, day) => sum + day.totalRequests, 0),
    performanceGrade: getPerformanceGrade(avgWebVitalsScore)
  };
}

export function getRealTimeMetrics() {
  // Simulate real-time performance data
  const currentLoad = 75 + Math.random() * 25;
  const responseTime = 150 + Math.random() * 100;
  const activeConnections = 120 + Math.floor(Math.random() * 80);
  
  return {
    currentLoad: Number(currentLoad.toFixed(1)),
    responseTime: Number(responseTime.toFixed(0)),
    activeConnections,
    requestsPerSecond: Number((15 + Math.random() * 10).toFixed(1)),
    cpuUsage: Number((60 + Math.random() * 25).toFixed(1)),
    memoryUsage: Number((70 + Math.random() * 20).toFixed(1)),
    errorRate: Number((Math.random() * 0.02).toFixed(4)),
    lastUpdated: new Date().toISOString()
  };
}

// Performance analysis functions
export function analyzePerformanceIssues() {
  const issues = [];
  const recentData = state.webVitals.slice(-7);
  const recentAppData = state.applicationMetrics.slice(-7);

  // Check Web Vitals issues
  recentData.forEach(day => {
    if (day.lcp > PERFORMANCE_THRESHOLDS.coreWebVitals.lcp.needs_improvement) {
      issues.push({
        type: 'critical',
        metric: 'Largest Contentful Paint',
        value: day.lcp,
        threshold: PERFORMANCE_THRESHOLDS.coreWebVitals.lcp.needs_improvement,
        impact: 'User experience significantly degraded',
        recommendation: 'Optimize image loading and reduce server response time'
      });
    }
    
    if (day.cls > PERFORMANCE_THRESHOLDS.coreWebVitals.cls.needs_improvement) {
      issues.push({
        type: 'warning',
        metric: 'Cumulative Layout Shift',
        value: day.cls,
        threshold: PERFORMANCE_THRESHOLDS.coreWebVitals.cls.needs_improvement,
        impact: 'Visual stability issues affecting user experience',
        recommendation: 'Reserve space for dynamic content and avoid layout shifts'
      });
    }
  });

  // Check application performance issues
  recentAppData.forEach(day => {
    if (day.responseTime > PERFORMANCE_THRESHOLDS.applicationMetrics.responseTime.needs_improvement) {
      issues.push({
        type: 'warning',
        metric: 'API Response Time',
        value: day.responseTime,
        threshold: PERFORMANCE_THRESHOLDS.applicationMetrics.responseTime.needs_improvement,
        impact: 'Slow API responses affecting user experience',
        recommendation: 'Optimize database queries and implement caching'
      });
    }
  });

  return issues;
}

export function getPerformanceGrade(score) {
  if (score >= 90) return { grade: 'A', color: '#28a745', description: 'Excellent' };
  if (score >= 80) return { grade: 'B', color: '#17a2b8', description: 'Good' };
  if (score >= 70) return { grade: 'C', color: '#ffc107', description: 'Needs Improvement' };
  if (score >= 60) return { grade: 'D', color: '#fd7e14', description: 'Poor' };
  return { grade: 'F', color: '#dc3545', description: 'Critical' };
}

export function generatePerformanceReport(options = {}) {
  const { dateFrom, dateTo, includeRecommendations = true } = options;
  
  const webVitals = getWebVitalsData({ dateFrom, dateTo });
  const appMetrics = getApplicationMetrics({ dateFrom, dateTo });
  const issues = includeRecommendations ? analyzePerformanceIssues() : [];
  
  return {
    reportId: `perf_report_${Date.now()}`,
    generatedAt: new Date().toISOString(),
    period: { from: dateFrom, to: dateTo },
    summary: getPerformanceSummary(),
    webVitals,
    applicationMetrics: appMetrics,
    issues,
    recommendations: issues.map(issue => issue.recommendation),
    budgetStatus: getPerformanceBudgets()
  };
}

// Data management
export function refreshData(days = 30) {
  state = generatePerformanceData(days);
  publish();
}

export function exportData() {
  return {
    performance: state,
    exportDate: new Date().toISOString(),
    thresholds: PERFORMANCE_THRESHOLDS
  };
}

export function importData(data) {
  if (data && data.performance) {
    state = data.performance;
    publish();
  }
}

// Utility functions
export function getPerformanceThresholds() {
  return PERFORMANCE_THRESHOLDS;
}

export function calculateApdexScore(responseTime, targetTime = 500, toleratingTime = 2000) {
  if (responseTime <= targetTime) return 1; // Satisfied
  if (responseTime <= toleratingTime) return 0.5; // Tolerating
  return 0; // Frustrated
}